import Box from '@mui/material/Box';
import TextField from '@material-ui/core/TextField';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setNewAppSlug, _newAppSlug } from '../../store/slices/editorSlice';
import { saveAppSlug } from '../../store/slices/organizationSlice';
import { _selectedLocation } from '../../store/slices/locationSlice';
import { _selectedOrganization } from '../../store/slices/organizationSlice';
import { FlatButton } from '../Buttons/FlatButton';
import { ViiisionTempDrawer } from '../ViiisionTempDrawer';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		text: {
			// width: 264,
			marginBottom: 20,
		},
		content: {
			display: 'flex',
			justifyContent: 'space-between',
			flexDirection: 'column',
			width: 557,
			height: '75%',
			marginLeft: 10,
		},
		buttonBox: {
			marginTop: 40,
			marginBottom: 75,
			display: 'flex',
			justifyContent: 'flex-end',
			alignItems: 'center',
			height: 80,
		},
		button: {
			height: 40,
			marginRight: 10,
		},
		formSection: {
			marginBottom: 24,
		},
		formField: {
			marginBottom: 16,
			width: '100%',
		},
		sectionTitle: {
			marginBottom: 12,
			fontWeight: 600,
		},
		readOnlyField: {
			'& .MuiInputBase-input': {
				color: theme.palette.text.secondary,
			},
		},
	})
);

export type AppInformationDrawerProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
};

// App Information Drawer component
// Similar to MLDrawer, this drawer allows users to edit app information
// The main feature is the app slug which will be saved to the organization collection
// Additional app information is displayed for context and to make use of the space
export const AppInformationDrawer = (props: AppInformationDrawerProps) => {
	const { open, setOpen } = { ...props };
	const classes = useStyles();
	const dispatch = useAppDispatch();
	const selectedLocation = useAppSelector(_selectedLocation);
	const selectedOrganization = useAppSelector(_selectedOrganization);
	const newAppSlug = useAppSelector(_newAppSlug);

	const saveAppSlugAction = bindActionCreators(saveAppSlug, dispatch);
	const updateNewAppSlug = bindActionCreators(setNewAppSlug, dispatch);
	const [enableButton, setEnableButton] = useState(false);

	// Initialize form data from location and organization
	const [formData, setFormData] = useState({
		appSlug: '',
		appName: '',
		companyName: '',
		contactName: '',
		phoneNumber: '',
		email: '',
		organizationEmail: '',
		subscriptionStatus: '',
	});

	// Error state for app slug validation
	const [appSlugError, setAppSlugError] = useState('');

	// Initialize form data when drawer opens or data changes
	useEffect(() => {
		if (open && selectedLocation && selectedOrganization) {
			const currentSlug = newAppSlug !== null ? newAppSlug : selectedOrganization?.appSlug || '';
			setFormData({
				appSlug: currentSlug,
				appName: selectedLocation?.name || '',
				companyName: selectedLocation?.companyName || selectedOrganization?.name || '',
				contactName: selectedLocation?.contactName || '',
				phoneNumber: selectedLocation?.phoneNumber || '',
				email: selectedLocation?.email || '',
				organizationEmail: selectedOrganization?.email || '',
				subscriptionStatus: selectedOrganization?.subscription_active ? 'Active' : 'Inactive',
			});
			// Clear error when drawer opens
			setAppSlugError('');
		}
	}, [open, selectedLocation, selectedOrganization, newAppSlug]);

	// Initialize newAppSlug when drawer opens
	useEffect(() => {
		if (open && selectedOrganization && newAppSlug === null) {
			updateNewAppSlug(selectedOrganization?.appSlug || '');
		}
	}, [open, selectedOrganization, newAppSlug, updateNewAppSlug]);

	const handleSlugChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		// Sanitize slug: only allow lowercase letters, numbers, and hyphens
		const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
		setFormData((prev) => ({ ...prev, appSlug: sanitized }));
		updateNewAppSlug(sanitized);
		setEnableButton(selectedOrganization?.appSlug !== sanitized);
		// Clear error when user starts typing
		setAppSlugError('');
	};

	const handleCancel = () => {
		updateNewAppSlug(null);
		setAppSlugError('');
		setOpen(false);
	};

	const handleSave = async () => {
		setAppSlugError('');
		try {
			await saveAppSlugAction();
			setEnableButton(false);
			setOpen(false);
		} catch (error: any) {
			// Error is shown via toast by axios interceptor
			// Extract and show the actual error message inline
			let errorMessage = 'An error occurred while saving. Please try again.';

			if (error?.response?.data) {
				const responseData = error.response.data;
				if (typeof responseData === 'string') {
					// Extract the error message before the pipe character
					const messageParts = responseData.split(' | ');
					if (messageParts.length > 0 && messageParts[0].trim()) {
						errorMessage = messageParts[0].trim();
					}
				} else if (typeof responseData === 'object' && responseData.message) {
					errorMessage = responseData.message;
				}
			} else if (error?.message) {
				errorMessage = error.message;
			}

			setAppSlugError(errorMessage);
			// Keep drawer open so user can fix and try again
		}
	};

	return (
		<ViiisionTempDrawer open={open} setOpen={setOpen} title={'App Information'}>
			<Box className={classes.content}>
				<Typography className={classes.text} variant="body2">
					Manage your app's information and settings. The app slug is used in your app's URL and can be
					customized to create a unique identifier for your app.
				</Typography>

				{/* App Slug Section */}
				<Box className={classes.formSection}>
					<Typography className={classes.sectionTitle} variant="subtitle2">
						APP SLUG
					</Typography>
					<TextField
						className={classes.formField}
						label="App Slug"
						value={formData.appSlug}
						onChange={handleSlugChange}
						placeholder="my-app-slug"
						helperText={
							appSlugError ||
							"Used in your app's URL. Only lowercase letters, numbers, and hyphens are allowed."
						}
						error={!!appSlugError}
						variant="outlined"
						fullWidth
					/>
				</Box>

				{/* App Details Section */}
				<Box className={classes.formSection}>
					<Typography className={classes.sectionTitle} variant="subtitle2">
						APP DETAILS
					</Typography>
					<TextField
						className={`${classes.formField} ${classes.readOnlyField}`}
						label="App Name"
						value={formData.appName}
						disabled
						helperText="The name of your app"
						variant="outlined"
						fullWidth
					/>
				</Box>

				{/* Contact Information Section */}
				<Box className={classes.formSection}>
					<Typography className={classes.sectionTitle} variant="subtitle2">
						CONTACT INFORMATION
					</Typography>
					<TextField
						className={`${classes.formField} ${classes.readOnlyField}`}
						label="Contact Name"
						value={formData.contactName}
						disabled
						helperText="Primary contact name"
						variant="outlined"
						fullWidth
					/>
					<TextField
						className={`${classes.formField} ${classes.readOnlyField}`}
						label="Phone Number"
						value={formData.phoneNumber}
						disabled
						helperText="Contact phone number"
						variant="outlined"
						fullWidth
					/>
					<TextField
						className={`${classes.formField} ${classes.readOnlyField}`}
						label="Email"
						value={formData.email}
						disabled
						helperText="Contact email address"
						variant="outlined"
						fullWidth
					/>
				</Box>

				<Box className={classes.buttonBox}>
					<FlatButton className={classes.button} variant="contained" color="primary" onClick={handleCancel}>
						Cancel
					</FlatButton>
					<FlatButton
						disabled={!enableButton}
						className={classes.button}
						variant="contained"
						color="primary"
						onClick={handleSave}
					>
						Save
					</FlatButton>
				</Box>
			</Box>
		</ViiisionTempDrawer>
	);
};

export default AppInformationDrawer;
